// src/components/shared/SmartSearch.js
// Reusable search component for Help Center and Business Playbook.
// Receives articles array, scores them against the query, shows ranked results.

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Search, FileText, X } from 'lucide-react';
import { trackEvent } from '../../utils/analytics';

// ---------------------------------------------------------------------------
// Scoring
// ---------------------------------------------------------------------------

function scoreArticle(article, query) {
  const queryLower = query.toLowerCase().trim();
  const queryWords = queryLower.split(/\s+/);
  let score = 0;

  // 1. Title match (highest weight - 10 points per word match)
  const titleLower = (article.title || '').toLowerCase();
  queryWords.forEach(word => {
    if (titleLower.includes(word)) score += 10;
  });
  // Exact title match bonus
  if (titleLower.includes(queryLower)) score += 20;

  // 2. AI intent phrase match (high weight - 8 points)
  const intentPhrases = article.searchMetadata?.intentPhrases || [];
  intentPhrases.forEach(phrase => {
    const phraseLower = phrase.toLowerCase();
    if (phraseLower.includes(queryLower) || queryLower.includes(phraseLower)) score += 8;
    queryWords.forEach(word => {
      if (phraseLower.includes(word)) score += 4;
    });
  });

  // 3. AI keyword match (medium weight - 5 points)
  const aiKeywords = article.searchMetadata?.aiKeywords || [];
  aiKeywords.forEach(keyword => {
    if (keyword.toLowerCase().includes(queryLower)) score += 5;
    queryWords.forEach(word => {
      if (keyword.toLowerCase().includes(word)) score += 2;
    });
  });

  // 4. Manual keywords match (medium weight - 4 points)
  const keywords = article.keywords || [];
  keywords.forEach(keyword => {
    if (keyword.toLowerCase().includes(queryLower)) score += 4;
  });

  // 5. Body content match (standard weight - 1 point per match)
  const contentText = (article.content || article.paragraphs?.join(' ') || '')
    .replace(/<[^>]*>/g, '')
    .toLowerCase();
  queryWords.forEach(word => {
    if (contentText.includes(word)) score += 1;
  });

  return score;
}

function getSnippet(article, maxLen = 100) {
  const raw = article.content || article.paragraphs?.[0] || '';
  const plain = raw.replace(/<[^>]*>/g, '').trim();
  if (plain.length <= maxLen) return plain;
  return plain.substring(0, maxLen).replace(/\s\S*$/, '') + '...';
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const SmartSearch = ({
  articles = [],
  onResultClick,
  placeholder = 'Search for help...',
  maxResults = 8,
  showCategoryBadge = true,
}) => {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Debounce 300ms
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(timer);
  }, [query]);

  // Close on click outside
  useEffect(() => {
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Close on Escape
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, []);

  const results = React.useMemo(() => {
    if (debouncedQuery.trim().length < 2) return [];
    return articles
      .map(article => ({ ...article, _score: scoreArticle(article, debouncedQuery) }))
      .filter(a => a._score > 0)
      .sort((a, b) => b._score - a._score)
      .slice(0, maxResults);
  }, [debouncedQuery, articles, maxResults]);

  const handleResultClick = useCallback((article) => {
    trackEvent('help_search', { query: debouncedQuery });
    setIsOpen(false);
    setQuery('');
    setDebouncedQuery('');
    onResultClick(article);
  }, [onResultClick, debouncedQuery]);

  const showResults = isOpen && debouncedQuery.trim().length >= 2;

  return (
    <div ref={containerRef} className="relative w-full max-w-xl mx-auto">
      {/* Search input */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setIsOpen(true); }}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder}
          className="w-full pl-12 pr-10 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white"
          style={{ height: '44px' }}
        />
        {query && (
          <span
            role="button"
            tabIndex={0}
            onClick={() => { setQuery(''); setDebouncedQuery(''); inputRef.current?.focus(); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { setQuery(''); setDebouncedQuery(''); inputRef.current?.focus(); } }}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
            style={{ userSelect: 'none' }}
          >
            <X className="w-4 h-4 text-gray-400" />
          </span>
        )}
      </div>

      {/* Results dropdown */}
      {showResults && (
        <div
          className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg border border-gray-200 overflow-hidden z-50"
          style={{ maxHeight: '400px', overflowY: 'auto', boxShadow: '0 10px 40px -5px rgba(0,0,0,0.15)' }}
        >
          {results.length > 0 ? (
            results.map((article) => (
              <span
                key={article.id || article.title}
                role="button"
                tabIndex={0}
                onClick={() => handleResultClick(article)}
                onKeyDown={(e) => e.key === 'Enter' && handleResultClick(article)}
                className="block w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer border-b border-gray-100 last:border-b-0"
                style={{ userSelect: 'none' }}
              >
                <div className="flex items-start gap-3">
                  <FileText className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-gray-900 text-sm">{article.title}</div>
                    {showCategoryBadge && article.categoryName && (
                      <div className="flex items-center gap-2 mt-0.5">
                        <span
                          className="inline-block px-2 py-0.5 text-xs font-medium rounded-full"
                          style={{
                            backgroundColor: article.categoryColor || '#F3F4F6',
                            color: article.categoryTextColor || '#6B7280',
                          }}
                        >
                          {article.categoryName}
                        </span>
                        {article.audienceLabel && (
                          <span className="text-xs text-gray-400">{article.audienceLabel}</span>
                        )}
                      </div>
                    )}
                    <p className="text-xs text-gray-500 mt-1 line-clamp-1">{getSnippet(article)}</p>
                  </div>
                </div>
              </span>
            ))
          ) : (
            <div className="px-4 py-6 text-center">
              <p className="text-sm text-gray-500">No results found for "{debouncedQuery}"</p>
              <p className="text-xs text-gray-400 mt-1">
                Try different keywords or{' '}
                <a href="mailto:support@divineframeworks.com" className="text-purple-600 hover:text-purple-700">
                  contact support
                </a>
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SmartSearch;
